import { useQuery } from "@tanstack/react-query";
import { QUERIES } from "modules/common/query";
import { API_ROUTES } from "utils/api-route.constants";

const VERSION_REQUEST_TIMEOUT_MS = 5_000;

export interface VersionResponse {
  tag: string;
  commit?: string;
  downtime: boolean;
}

export const useVersion = () => {
  return useQuery({
    queryKey: [QUERIES.APP_VERSION],
    queryFn: getVersion,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });
};

async function getVersion(): Promise<VersionResponse> {
  const controller = new AbortController();
  const timeout = window.setTimeout(
    () => controller.abort(),
    VERSION_REQUEST_TIMEOUT_MS
  );

  try {
    const response = await fetch(API_ROUTES.VERSION.GET, {
      cache: "no-store",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`Version request failed with ${response.status}`);
    }

    const data: unknown = await response.json();
    if (
      typeof data !== "object" ||
      data === null ||
      typeof (data as Partial<VersionResponse>).tag !== "string" ||
      typeof (data as Partial<VersionResponse>).downtime !== "boolean"
    ) {
      throw new Error("Version response is missing availability state");
    }

    return data as VersionResponse;
  } finally {
    window.clearTimeout(timeout);
  }
}
